import { useMemo } from "react";
import {
  Ard,
  UseCase,
  UslLine,
} from "@amzn/fuse-accounting-central-datastore-client";

/**
 * Path scheme used to identify modified fields:
 *
 * - Top-level ARD field         : "product", "term", ...
 * - Use case attribute           : "usecases[<i>].name", "usecases[<i>].altName"
 * - USL line cell                : "usecases[<i>].uslLines[<j>].<field>"
 */
export const fieldPath = {
  /** Top-level ARD field path. */
  ard: (field: keyof Ard): string => String(field),
  /** Use-case attribute path (e.g., name / altName). */
  useCase: (useCaseIndex: number, field: keyof UseCase): string =>
    `usecases[${useCaseIndex}].${String(field)}`,
  /** USL line cell path. */
  uslLine: (
    useCaseIndex: number,
    lineIndex: number,
    field: keyof UslLine,
  ): string =>
    `usecases[${useCaseIndex}].uslLines[${lineIndex}].${String(field)}`,
};

/** Top-level ARD fields we consider for change tracking. */
const TRACKED_ARD_FIELDS: (keyof Ard)[] = [
  "product",
  "template",
  "term",
  "billingMode",
  "businessCountryCode",
  "buyingCustomerCountryCode",
  "functionalCurrency",
  "localCurrency",
];

/** Use-case attributes we consider for change tracking. */
const TRACKED_USE_CASE_FIELDS: (keyof UseCase)[] = ["name", "altName"];

/** USL line fields we consider for change tracking. */
const TRACKED_USL_FIELDS: (keyof UslLine)[] = [
  "companyCode",
  "location",
  "costCenter",
  "accountNumber",
  "productLine",
  "channel",
  "project",
  "debitOrCredit",
  "glLineDescriptionSuffix",
  "subscriptionProductLine",
  "invoiceAccountingClass",
];

/**
 * Treat `null`, `undefined`, and empty string as equivalent so an empty
 * baseline value vs an empty current value isn't reported as a change.
 */
const sameish = (a: unknown, b: unknown): boolean => {
  const na = a === undefined || a === null || a === "" ? null : a;
  const nb = b === undefined || b === null || b === "" ? null : b;
  return na === nb;
};

/**
 * Diff `formData` against `baseline` and produce a set of field paths whose
 * values have been modified by the user.
 *
 * Returned paths follow the conventions documented on {@link fieldPath}.
 */
export const computeChangedFields = (
  formData: Ard | null,
  baseline: Ard | null,
): Set<string> => {
  const changed = new Set<string>();
  if (!formData || !baseline) return changed;

  // Top-level ARD fields
  for (const field of TRACKED_ARD_FIELDS) {
    if (!sameish(formData[field], baseline[field])) {
      changed.add(fieldPath.ard(field));
    }
  }

  const formUseCases = formData.usecases || [];
  const baseUseCases = baseline.usecases || [];

  // For each use case in the current form, diff against the matching
  // baseline use case (by index).
  formUseCases.forEach((formUC, ucIndex) => {
    const baseUC: UseCase | undefined = baseUseCases[ucIndex];

    // Use-case attributes
    for (const field of TRACKED_USE_CASE_FIELDS) {
      if (!sameish(formUC?.[field], baseUC?.[field])) {
        changed.add(fieldPath.useCase(ucIndex, field));
      }
    }

    // USL lines
    const formLines = formUC?.uslLines || [];
    const baseLines = baseUC?.uslLines || [];

    formLines.forEach((formLine, lineIndex) => {
      const baseLine: UslLine | undefined = baseLines[lineIndex];
      for (const field of TRACKED_USL_FIELDS) {
        if (!sameish(formLine?.[field], baseLine?.[field])) {
          changed.add(fieldPath.uslLine(ucIndex, lineIndex, field));
        }
      }
    });

    // If the current use case has more lines than baseline, mark all
    // fields of the new lines as changed.
    if (formLines.length > baseLines.length) {
      for (let i = baseLines.length; i < formLines.length; i++) {
        for (const field of TRACKED_USL_FIELDS) {
          changed.add(fieldPath.uslLine(ucIndex, i, field));
        }
      }
    }

    // Detect deleted lines
    if (baseLines.length > formLines.length) {
      for (let i = formLines.length; i < baseLines.length; i++) {
        for (const field of TRACKED_USL_FIELDS) {
          changed.add(fieldPath.uslLine(ucIndex, i, field));
        }
      }
    }
  });

  // Detect deleted use cases
  for (let i = formUseCases.length; i < baseUseCases.length; i++) {
    for (const field of TRACKED_USE_CASE_FIELDS) {
      changed.add(fieldPath.useCase(i, field));
    }
  }

  return changed;
};

/**
 * React hook returning a memoized "changed fields" calculator over the given
 * baseline. Exposes both the underlying `Set` and a convenience
 * `isFieldChanged(path)` predicate.
 */
export const useChangedFields = (
  formData: Ard | null,
  baseline: Ard | null,
) => {
  const changedFields = useMemo(
    () => computeChangedFields(formData, baseline),
    [formData, baseline],
  );

  const isFieldChanged = (path: string): boolean => changedFields.has(path);

  return { changedFields, isFieldChanged };
};
